'use client'

import { cn } from '@/lib/utils'
import { ChatRequestOptions, JSONValue, Message } from 'ai'
import { useEffect, useMemo, useState } from 'react'
import { RenderMessage } from './render-message'
import { ToolSection } from './tool-section'
import { Spinner } from './ui/spinner'
import * as Tabs from '@radix-ui/react-tabs'
import dynamic from 'next/dynamic'
import { useAppSelector } from '@/lib/store/hooks'
import { addMACD } from '@/lib/tv/bridge'
// Import section structure interface
interface ChatSection {
  id: string
  userMessage: Message
  assistantMessages: Message[]
}

const TradingViewWrapper = dynamic(() => import('@/components/tv/trading-view-wrapper'), {
  ssr: false,
});

interface ChatMessagesProps {
  sections: ChatSection[] // Changed from messages to sections
  data: JSONValue[] | undefined
  onQuerySelect: (query: string) => void
  isLoading: boolean
  chatId?: string
  addToolResult?: (params: { toolCallId: string; result: any }) => void
  /** Ref for the scroll container */
  scrollContainerRef: React.RefObject<HTMLDivElement>
  onUpdateMessage?: (messageId: string, newContent: string) => Promise<void>
  reload?: (
    messageId: string,
    options?: ChatRequestOptions
  ) => Promise<string | null | undefined>
}

export function ChatMessages({
  sections,
  data,
  onQuerySelect,
  isLoading,
  chatId,
  addToolResult,
  scrollContainerRef,
  onUpdateMessage,
  reload
}: ChatMessagesProps) {
  const [openStates, setOpenStates] = useState<Record<string, boolean>>({})
  const manualToolCallId = 'manual-tool-call'
  const advancedModeEnabled = useAppSelector((s) => s.advancedMode.value);

  useEffect(() => {
    // Open manual tool call when the last section is a user message
    if (sections.length > 0) {
      const lastSection = sections[sections.length - 1]
      if (lastSection.userMessage.role === 'user') {
        setOpenStates({ [manualToolCallId]: true })
      }
    }
  }, [sections])

  // get last tool data for manual tool call
  const lastToolData = useMemo(() => {
    if (!data || !Array.isArray(data) || data.length === 0) return null

    const lastItem = data[data.length - 1] as {
      type: 'tool_call'
      data: {
        toolCallId: string
        state: 'call' | 'result'
        toolName: string
        args: string
      }
    }

    if (lastItem.type !== 'tool_call') return null

    const toolData = lastItem.data
    return {
      state: 'call' as const,
      toolCallId: toolData.toolCallId,
      toolName: toolData.toolName,
      args: toolData.args ? JSON.parse(toolData.args) : undefined
    }
  }, [data])

  if (!sections.length) return null

  // Get all messages as a flattened array
  const allMessages = sections.flatMap(section => [
    section.userMessage,
    ...section.assistantMessages
  ])

  const lastUserIndex =
    allMessages.length -
    1 -
    [...allMessages].reverse().findIndex(msg => msg.role === 'user')

  // Check if loading indicator should be shown
  const showLoading =
    isLoading &&
    sections.length > 0 &&
    sections[sections.length - 1].assistantMessages.length === 0

  const getIsOpen = (id: string) => {
    if (id.includes('call')) {
      return openStates[id] ?? true
    }
    const baseId = id.endsWith('-related') ? id.slice(0, -8) : id
    const index = allMessages.findIndex(msg => msg.id === baseId)
    return openStates[id] ?? index >= lastUserIndex
  }

  const handleOpenChange = (id: string, open: boolean) => {
    setOpenStates(prev => ({
      ...prev,
      [id]: open
    }))
  }

  return (
    // <Tabs.Root defaultValue="tab1" className="flex flex-col flex-1 min-h-0">
    //   <Tabs.List aria-label="Asset Analysis" className="sticky top-0 z-10 flex gap-2 border-b bg-background px-4 py-2">
    //     <Tabs.Trigger value='tab1' className="data-[state=active]:font-semibold px-2 py-1 rounded hover:bg-muted">
    //       Fundamental Analysis
    //     </Tabs.Trigger>
    //     {advancedModeEnabled && (
    //       <Tabs.Trigger value='tab2' className="data-[state=active]:font-semibold px-2 py-1 rounded hover:bg-muted">
    //         Chart
    //       </Tabs.Trigger>
    //     )}
    //   </Tabs.List>
    //   <Tabs.Content value='tab1' className="hidden data-[state=active]:flex min-h-0 flex-1 overflow-hidden">
    //     <div
    //       id="scroll-container"
    //       ref={scrollContainerRef}
    //       role="list"
    //       aria-roledescription="chat messages"
    //       className={cn(
    //         'relative size-full pt-12 h-full overflow-y-auto',
    //         sections.length > 0 ? 'flex-1 overflow-y-auto' : ''
    //       )}
    //     >
    //       <div className="relative mx-auto w-full max-w-3xl px-4">
    //         {sections.map((section, sectionIndex) => (
    //           <div
    //             key={section.id}
    //             id={`section-${section.id}`}
    //             className="chat-section mb-8"
    //             style={
    //               sectionIndex === sections.length - 1
    //                 ? { minHeight: 'calc(-228px + 100dvh)' }
    //                 : {}
    //             }
    //           >
    //             {/* User message */}
    //             <div className="flex flex-col gap-4 mb-4">
    //               <RenderMessage
    //                 message={section.userMessage}
    //                 messageId={section.userMessage.id}
    //                 getIsOpen={getIsOpen}
    //                 onOpenChange={handleOpenChange}
    //                 onQuerySelect={onQuerySelect}
    //                 chatId={chatId}
    //                 addToolResult={addToolResult}
    //                 onUpdateMessage={onUpdateMessage}
    //                 reload={reload}
    //               />
    //               {showLoading && <Spinner />}
    //             </div>

    //             {/* Assistant messages */}
    //             {section.assistantMessages.map(assistantMessage => (
    //               <div key={assistantMessage.id} className="flex flex-col gap-4">
    //                 <RenderMessage
    //                   message={assistantMessage}
    //                   messageId={assistantMessage.id}
    //                   getIsOpen={getIsOpen}
    //                   onOpenChange={handleOpenChange}
    //                   onQuerySelect={onQuerySelect}
    //                   chatId={chatId}
    //                   addToolResult={addToolResult}
    //                   onUpdateMessage={onUpdateMessage}
    //                   reload={reload}
    //                 />
    //               </div>
    //             ))}
    //           </div>
    //         ))}

    //         {showLoading && lastToolData && (
    //           <ToolSection
    //             key={manualToolCallId}
    //             tool={lastToolData}
    //             isOpen={getIsOpen(manualToolCallId)}
    //             onOpenChange={open => handleOpenChange(manualToolCallId, open)}
    //             addToolResult={addToolResult}
    //           />
    //         )}
    //       </div>
    //     </div>
    //   </Tabs.Content>
    //   {advancedModeEnabled && (
    //     <Tabs.Content value='tab2' className="hidden data-[state=active]:flex flex-col flex-1 min-h-0">
    //       <div className="w-full flex-1 min-h-0 overflow-hidden">
    //         <TradingViewWrapper />
    //       </div>
    //     </Tabs.Content>
    //   )}

    // </Tabs.Root>
    <div
      id="scroll-container"
      ref={scrollContainerRef}
      role="list"
      aria-roledescription="chat messages"
      className={cn(
        'relative size-full pt-12 h-full overflow-y-auto',
        sections.length > 0 ? 'flex-1 overflow-y-auto' : ''
      )}
    >
      <div className="relative mx-auto w-full max-w-3xl px-4">
        {sections.map((section, sectionIndex) => (
          <div
            key={section.id}
            id={`section-${section.id}`}
            className="chat-section mb-8"
            style={
              sectionIndex === sections.length - 1
                ? { minHeight: 'calc(-228px + 100dvh)' }
                : {}
            }
          >
            {/* User message */}
            <div className="flex flex-col gap-4 mb-4">
              <RenderMessage
                message={section.userMessage}
                messageId={section.userMessage.id}
                getIsOpen={getIsOpen}
                onOpenChange={handleOpenChange}
                onQuerySelect={onQuerySelect}
                chatId={chatId}
                addToolResult={addToolResult}
                onUpdateMessage={onUpdateMessage}
                reload={reload}
              />
              {showLoading && <Spinner />}
            </div>

            {/* Assistant messages */}
            {section.assistantMessages.map(assistantMessage => (
              <div key={assistantMessage.id} className="flex flex-col gap-4">
                <RenderMessage
                  message={assistantMessage}
                  messageId={assistantMessage.id}
                  getIsOpen={getIsOpen}
                  onOpenChange={handleOpenChange}
                  onQuerySelect={onQuerySelect}
                  chatId={chatId}
                  addToolResult={addToolResult}
                  onUpdateMessage={onUpdateMessage}
                  reload={reload}
                />
              </div>
            ))}
          </div>
        ))}

        {showLoading && lastToolData && (
          <ToolSection
            key={manualToolCallId}
            tool={lastToolData}
            isOpen={getIsOpen(manualToolCallId)}
            onOpenChange={open => handleOpenChange(manualToolCallId, open)}
            addToolResult={addToolResult}
          />
        )}
      </div>
    </div>

  )
}
